/**
 * Error Handler Hook
 * 
 * Provides comprehensive error handling with:
 * - Unified error display through notification system
 * - Automatic retry for network errors
 * - Validation error handling
 * - Context-aware error messages
 */

import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../components/feedback/NotificationSystem';
import { authService } from '../services/authService';
import {
  normalizeError,
  extractValidationErrors,
  formatValidationErrors,
  getErrorNotificationOptions,
  isRetryableError,
  retryWithBackoff,
  waitForConnection,
  isOnline,
  ErrorDetails,
  ValidationError,
  RetryOptions
} from '../utils/errorHandling';
import {
  getErrorMessageKey,
  resolveNotificationMessage,
  resolveNotificationOptions,
} from '../utils/localizeNotificationMessage';

export interface ErrorHandlerOptions {
  context?: string;
  showNotification?: boolean;
  logError?: boolean;
  retryable?: boolean;
  retryOptions?: Partial<RetryOptions>;
}

export interface ValidationErrorHandlerOptions {
  showFieldErrors?: boolean;
  showNotificationBackup?: boolean;
  context?: string;
}

export interface NetworkErrorHandlerOptions {
  showRetryAction?: boolean;
  autoRetry?: boolean;
  retryOptions?: Partial<RetryOptions>;
  context?: string;
}

export function useErrorHandler() {
  const { t } = useTranslation('notifications');
  const { showError, showWarning, showInfo } = useNotifications();
  const retryAttempts = useRef<Map<string, number>>(new Map());

  /**
   * Handle general errors with unified notification display
   */
  const handleError = useCallback((
    error: any,
    options: ErrorHandlerOptions = {}
  ) => {
    const {
      context,
      showNotification = true,
      logError = true,
      retryable = false
    } = options;

    // Normalize the error
    const normalizedError = normalizeError(error, context);

    // Log error for debugging
    if (logError) {
      console.error(`[ErrorHandler${context ? ` - ${context}` : ''}]:`, {
        normalizedError,
        originalError: error,
        timestamp: new Date().toISOString()
      });
    }

    // Show notification if requested
    if (showNotification) {
      const messageKey = getErrorMessageKey(normalizedError);
      const displayMessage = messageKey
        ? t(messageKey)
        : resolveNotificationMessage(normalizedError.message, t);
      const notificationOptions = resolveNotificationOptions(
        getErrorNotificationOptions(normalizedError),
        t
      );
      
      // Add retry action for retryable errors
      if (retryable && isRetryableError(error)) {
        notificationOptions!.actions = [
          ...(notificationOptions?.actions || []),
          {
            label: t('retry.action'),
            action: () => {
              console.log('Retry requested for error:', normalizedError);
            },
            style: 'primary'
          }
        ];
      }

      showError(displayMessage, notificationOptions);
    }

    return normalizedError;
  }, [showError, t]);

  /**
   * Handle validation errors with field-specific display
   */
  const handleValidationErrors = useCallback((
    error: any,
    options: ValidationErrorHandlerOptions = {}
  ) => {
    const {
      showFieldErrors = true,
      showNotificationBackup = true,
      context = 'validation'
    } = options;

    const validationErrors = extractValidationErrors(error);
    
    if (validationErrors.length === 0) {
      // Fallback to general error handling
      return handleError(error, { context });
    }

    // Format errors for field display
    const formattedErrors = formatValidationErrors(validationErrors);

    // Show notification backup if requested
    if (showNotificationBackup) {
      const firstError = validationErrors[0];
      showError(firstError.message, {
        title: t('errors.validationTitle'),
        persistent: false,
        duration: 6000,
        context
      });
    }

    // Log validation errors
    console.warn(`[ValidationError - ${context}]:`, {
      validationErrors,
      formattedErrors,
      originalError: error
    });

    return {
      validationErrors,
      formattedErrors,
      hasErrors: validationErrors.length > 0
    };
  }, [handleError, showError]);

  /**
   * Handle network errors with retry capabilities
   */
  const handleNetworkError = useCallback(async (
    error: any,
    retryFn?: () => Promise<any>,
    options: NetworkErrorHandlerOptions = {}
  ) => {
    const {
      showRetryAction = true,
      autoRetry = false,
      retryOptions,
      context = 'network'
    } = options;

    const normalizedError = normalizeError(error, context);

    // Check if we're offline
    if (!isOnline()) {
      showError(t('connection.lost'), {
        title: t('connection.offline'),
        persistent: true,
        actions: [
          {
            label: t('connection.checkConnection'),
            action: async () => {
              showInfo(t('connection.checking'), {
                title: t('connection.checking'),
                duration: 3000
              });
              
              const connected = await waitForConnection(10000);
              if (connected) {
                showInfo(t('connection.restored'), {
                  title: t('connection.backOnline'),
                  duration: 3000
                });
              } else {
                showError(t('connection.reconnectFailed'), {
                  title: t('error')
                });
              }
            },
            style: 'primary'
          }
        ],
        context
      });
      return normalizedError;
    }

    // Auto retry if enabled and function provided
    if (autoRetry && retryFn && isRetryableError(error)) {
      try {
        showInfo(t('retry.inProgress'), {
          title: t('retry.action'),
          duration: 3000,
          context
        });

        const result = await retryWithBackoff(retryFn, retryOptions);
        
        showInfo(t('retry.success'), {
          title: t('success'),
          duration: 3000,
          context
        });

        return result;
      } catch (retryError) {
        // Fall through to show error with retry action
        console.error('Auto retry failed:', retryError);
      }
    }

    // Show error with retry action
    const displayMessage = getErrorMessageKey(normalizedError)
      ? t(getErrorMessageKey(normalizedError)!)
      : resolveNotificationMessage(normalizedError.message, t);
    const notificationOptions = resolveNotificationOptions(
      getErrorNotificationOptions(normalizedError),
      t
    );
    
    if (showRetryAction && retryFn && isRetryableError(error)) {
      notificationOptions!.actions = [
        {
          label: t('retry.action'),
          action: async () => {
            try {
              showInfo(t('retry.inProgress'), {
                title: t('retry.action'),
                duration: 3000,
                context
              });

              await retryFn();
              
              showInfo(t('retry.success'), {
                title: t('success'),
                duration: 3000,
                context
              });
            } catch (retryError) {
              handleError(retryError, { context: `${context}_retry` });
            }
          },
          style: 'primary'
        }
      ];
    }

    showError(displayMessage, notificationOptions);
    return normalizedError;
  }, [showError, showInfo, handleError, t]);

  /**
   * Handle API errors with context-specific handling
   */
  const handleApiError = useCallback((
    error: any,
    context: string = 'api',
    retryFn?: () => Promise<any>
  ) => {
    // Check if it's a validation error
    if (error?.statusCode === 422 || error?.errors) {
      return handleValidationErrors(error, { context });
    }

    // Check if it's a network error
    if (error?.statusCode >= 500 || error?.name === 'NetworkError' || !isOnline()) {
      return handleNetworkError(error, retryFn, { context });
    }

    // Handle authentication errors
    if (error?.statusCode === 401) {
      // CRITICAL: If redirect has already been initiated, skip all UI operations to prevent page freezing
      if (error?.redirected || error?.isSessionExpired) {
        console.log('useErrorHandler: Redirect already in progress, skipping error notification');
        return normalizeError(error, context);
      }

      // Check if redirect is in progress globally
      const redirectInProgress = (window as any).__sessionRedirectInProgress;
      if (redirectInProgress) {
        console.log('useErrorHandler: Global redirect flag detected, skipping error notification');
        return normalizeError(error, context);
      }

      // Only show notification if redirect hasn't happened yet.
      // Learner idle time does not sign the user out. Security failures and admin sessions do.
      void import('../utils/sessionExpirationPolicy').then(({ handleClientUnauthorized }) => {
        handleClientUnauthorized(error);
      }).catch((redirectError) => {
        console.error('useErrorHandler: Failed to apply session policy:', redirectError);
      });
      
      return normalizeError(error, context);
    }

    // Handle authorization errors
    if (error?.statusCode === 403) {
      showError(t('forbidden'), {
        title: t('error'),
        persistent: true,
        context
      });
      return normalizeError(error, context);
    }

    // Handle other API errors
    return handleError(error, { context });
  }, [handleError, handleValidationErrors, handleNetworkError, showError, t]);

  /**
   * Handle form submission errors
   */
  const handleFormError = useCallback((
    error: any,
    formContext: string = 'form'
  ) => {
    return handleValidationErrors(error, {
      showFieldErrors: true,
      showNotificationBackup: true,
      context: formContext
    });
  }, [handleValidationErrors]);

  /**
   * Handle file upload errors
   */
  const handleUploadError = useCallback((
    error: any,
    retryFn?: () => Promise<any>
  ) => {
    const context = 'file_upload';
    
    // File too large (match explicit codes / English messages — do not use broad "size" match: it catches "Image size must be less than...")
    const msg = typeof error?.message === 'string' ? error.message : '';
    const lower = msg.toLowerCase();
    if (
      error?.code === 'FILE_TOO_LARGE' ||
      error?.code === 'PHOTO_TOO_LARGE' ||
      /exceeds the maximum|file size exceeds|too large|image size must be less than/i.test(lower)
    ) {
      showError(msg || t('errors.uploadFileTooLarge'), {
        title: t('errors.uploadTitle'),
        persistent: false,
        duration: 6000,
        context
      });
      return normalizeError(error, context);
    }

    if (
      error?.code === 'INVALID_FILE_TYPE' ||
      error?.code === 'PHOTO_INVALID_TYPE' ||
      /invalid file type|invalid content type|not a valid image|only .*files are allowed/i.test(lower)
    ) {
      showError(
        msg || t('errors.uploadInvalidType'),
        {
          title: t('errors.invalidFileTypeTitle'),
          persistent: false,
          duration: 6000,
          context
        }
      );
      return normalizeError(error, context);
    }

    // Handle as network error with retry
    return handleNetworkError(error, retryFn, { context });
  }, [showError, handleNetworkError]);

  /**
   * Clear retry attempts for a specific context
   */
  const clearRetryAttempts = useCallback((context: string) => {
    retryAttempts.current.delete(context);
  }, []);

  /**
   * Get retry count for a specific context
   */
  const getRetryCount = useCallback((context: string): number => {
    return retryAttempts.current.get(context) || 0;
  }, []);

  return {
    handleError,
    handleValidationErrors,
    handleNetworkError,
    handleApiError,
    handleFormError,
    handleUploadError,
    clearRetryAttempts,
    getRetryCount,
    isOnline: isOnline()
  };
}